import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getPaymentStatus } from '@/lib/api';
import { getCertStatus } from '@/lib/certAccess';

const CERT_PATHS = {
  'ai-900': '/ai-900-exam-prep',
  'ai-102': '/ai-102-exam-prep',
  'az-900': '/az-900-exam-prep',
  'dp-100': '/dp-100-exam-prep',
  'ab-900': '/ab-900-exam-prep',
};

const CERT_LABELS = {
  'ai-900': 'AI-900: Azure AI Fundamentals',
  'ai-102': 'AI-102: Azure AI Engineer Associate',
  'az-900': 'AZ-900: Azure Fundamentals',
  'dp-100': 'DP-100: Azure Data Scientist Associate',
  'ab-900': 'AB-900: Copilot & Agent Administration Fundamentals',
};

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const certFromQs = searchParams.get('cert');
  const [status, setStatus] = useState('loading');
  const [attempts, setAttempts] = useState(0);
  const [certCode, setCertCode] = useState(certFromQs || null);

  const pollPaymentStatus = useCallback(async () => {
    if (attempts >= 6) {
      setStatus('timeout');
      return;
    }
    try {
      // Cert flow first (also flips entitlement on the backend)
      const certResp = await getCertStatus(sessionId);
      if (certResp?.cert_code) setCertCode(certResp.cert_code);
      if (certResp?.payment_status === 'paid') {
        setStatus('success');
        return;
      }

      // Fallback to ERP-style status
      const response = await getPaymentStatus(sessionId);
      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        setAttempts((prev) => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      setStatus('error');
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (sessionId) pollPaymentStatus();
  }, [sessionId, pollPaymentStatus]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center" data-testid="payment-loading">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-violet-600 animate-spin mx-auto mb-4" />
          <p className="text-lg text-slate-600">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    const certPath = certCode ? CERT_PATHS[certCode] : null;
    const certLabel = certCode ? CERT_LABELS[certCode] : null;
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center" data-testid="payment-success">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Payment Successful!</h1>
          {certLabel ? (
            <p className="text-slate-600 mb-8">
              You've unlocked <strong>{certLabel}</strong> Pro access — Exam Simulation,
              Weak-Areas practice, and the AI Coach Chat are now live for your account.
            </p>
          ) : (
            <p className="text-slate-600 mb-8">
              Thank you for enrolling with LearnQuanta. You will receive a confirmation
              email with program details and next steps.
            </p>
          )}
          <div className="flex flex-col gap-4">
            {certPath && (
              <Link to={certPath}>
                <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white w-full" data-testid="goto-cert-btn">
                  Open Exam Coach
                </Button>
              </Link>
            )}
            <Link to="/"><Button variant="outline" className="w-full">Back to Home</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 flex items-center justify-center" data-testid="payment-error">
      <div className="max-w-md mx-auto text-center p-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-4">Payment Status</h1>
        <p className="text-slate-600 mb-8">
          We couldn't verify your payment status. Please check your email for confirmation
          or contact support.
        </p>
        <Link to="/contact"><Button className="btn-primary">Contact Support</Button></Link>
      </div>
    </div>
  );
}
